import { type ReactNode, useMemo, useState, type CSSProperties } from "react";
import { ChevronLeft, ChevronRight, ChevronsUpDown, ChevronDown, ChevronUp, Search } from "lucide-react";

export type SortDir = "asc" | "desc";

export type ColumnDef<T> = {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  accessor?: (row: T) => string | number | null | undefined;
  align?: "left" | "right" | "center";
  width?: string | number;
  minWidth?: string | number;
  sortable?: boolean;
  className?: string;
  headerClassName?: string;
  /** Allow content to wrap (default: nowrap). */
  wrap?: boolean;
};

type DataTableProps<T> = {
  data: T[];
  columns: ColumnDef<T>[];
  rowKey: (row: T) => string | number;
  loading?: boolean;
  emptyMessage?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  toolbarActions?: ReactNode;
  filtersSlot?: ReactNode;
  searchable?: boolean;
  searchPlaceholder?: string;
  loadMore?: { hasMore: boolean; onLoadMore: () => void; loading?: boolean };
  pageSize?: number;
  onRowClick?: (row: T) => void;
  initialSort?: { key: string; dir: SortDir };
  totalLabel?: ReactNode;
};

export function DataTable<T>({
  data,
  columns,
  rowKey,
  loading,
  emptyMessage = "Aucun résultat",
  title,
  subtitle,
  toolbarActions,
  filtersSlot,
  searchable,
  searchPlaceholder = "Rechercher…",
  loadMore,
  pageSize = 25,
  onRowClick,
  initialSort,
  totalLabel,
}: DataTableProps<T>) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(initialSort ?? null);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(pageSize);

  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return data;
    const q = query.trim().toLowerCase();
    return data.filter((row) =>
      columns.some((c) => {
        const v = c.accessor ? c.accessor(row) : null;
        if (v === null || v === undefined) return false;
        return String(v).toLowerCase().includes(q);
      }),
    );
  }, [data, query, searchable, columns]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col || !col.accessor) return filtered;
    const acc = col.accessor;
    const sign = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = acc(a);
      const bv = acc(b);
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * sign;
      return String(av).localeCompare(String(bv)) * sign;
    });
  }, [filtered, sort, columns]);

  const useClientPagination = !loadMore;
  const totalRows = sorted.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / perPage));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = useClientPagination
    ? sorted.slice(safePage * perPage, safePage * perPage + perPage)
    : sorted;

  const toggleSort = (key: string) => {
    const col = columns.find((c) => c.key === key);
    if (!col?.sortable) return;
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const showHeader = title || subtitle || toolbarActions || searchable;

  return (
    <div className="bg-white rounded-xl shadow-card overflow-hidden">
      {showHeader && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 px-4 sm:px-6 py-4 sm:py-5 border-b border-[#eff2f5]">
          <div className="min-w-0">
            {title && <div className="text-base sm:text-[1.075rem] font-semibold text-[#181c32]">{title}</div>}
            {subtitle && <div className="text-sm text-[#7e8299] mt-1">{subtitle}</div>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {searchable && (
              <div className="relative w-full sm:w-auto">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#a1a5b7]" />
                <input
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(0);
                  }}
                  placeholder={searchPlaceholder}
                  className="pl-10 pr-3 py-2.5 text-sm bg-[#f5f8fa] rounded-lg w-full sm:w-64 focus:outline-none focus:bg-[#eef3f7] transition-colors"
                />
              </div>
            )}
            {toolbarActions}
          </div>
        </div>
      )}

      {filtersSlot && (
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-[#eff2f5] flex flex-wrap gap-2 sm:gap-3 items-center bg-[#fcfcfd]">
          {filtersSlot}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse" style={{ minWidth: "100%" }}>
          <thead>
            <tr>
              {columns.map((c, idx) => (
                <th
                  key={c.key}
                  style={colStyle(c)}
                  onClick={() => c.sortable && toggleSort(c.key)}
                  className={[
                    idx === 0 ? "pl-6" : "pl-4",
                    idx === columns.length - 1 ? "pr-6" : "pr-4",
                    "py-4",
                    alignClass(c.align),
                    "text-xs uppercase tracking-wider font-bold text-[#3f4254]",
                    "bg-[#f9fafb] border-b-2 border-[#e4e6ef]",
                    "whitespace-nowrap select-none",
                    c.sortable ? "cursor-pointer hover:text-brand-700" : "",
                    c.headerClassName ?? "",
                  ].filter(Boolean).join(" ")}
                >
                  <span
                    className={`inline-flex items-center gap-1.5 ${
                      c.align === "right" ? "justify-end" : c.align === "center" ? "justify-center" : ""
                    }`}
                  >
                    {c.header}
                    {c.sortable && sortIcon(c.key, sort)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, rIdx) => (
              <tr
                key={rowKey(row)}
                onClick={() => onRowClick?.(row)}
                className={[
                  "transition-colors hover:bg-[#f9f9f9]",
                  onRowClick ? "cursor-pointer" : "",
                ].filter(Boolean).join(" ")}
              >
                {columns.map((c, idx) => (
                  <td
                    key={c.key}
                    style={colStyle(c)}
                    className={[
                      idx === 0 ? "pl-6" : "pl-4",
                      idx === columns.length - 1 ? "pr-6" : "pr-4",
                      "py-4 align-middle text-sm text-[#181c32]",
                      alignClass(c.align),
                      c.wrap ? "" : "whitespace-nowrap",
                      "border-b border-[#eff2f5]",
                      rIdx === pageRows.length - 1 ? "border-b-0" : "",
                      c.className ?? "",
                    ].filter(Boolean).join(" ")}
                  >
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
            {!loading && pageRows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-6 py-16 text-center text-sm text-[#7e8299]">
                  {emptyMessage}
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center text-sm text-[#7e8299]">
                  Chargement…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {(useClientPagination && totalRows > 0) || loadMore ? (
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-[#eff2f5] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-[#5e6278]">
          {useClientPagination ? (
            <>
              <div className="flex items-center gap-2 text-xs">
                <span className="hidden sm:inline">Lignes par page</span>
                <span className="sm:hidden">Par page</span>
                <select
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  className="bg-[#f5f8fa] rounded-md px-2.5 py-1.5 text-xs font-medium focus:outline-none"
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 text-xs">
                <span>
                  {totalRows === 0 ? "0" : `${safePage * perPage + 1}-${Math.min((safePage + 1) * perPage, totalRows)}`} sur {totalRows}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={safePage === 0}
                    className="p-1.5 rounded-md hover:bg-[#f5f8fa] disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Précédent"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="px-2 font-medium whitespace-nowrap">
                    <span className="hidden sm:inline">Page </span>{safePage + 1} / {pageCount}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    disabled={safePage >= pageCount - 1}
                    className="p-1.5 rounded-md hover:bg-[#f5f8fa] disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Suivant"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="text-xs">{totalLabel ?? `${totalRows} ligne${totalRows > 1 ? "s" : ""}`}</div>
              {loadMore?.hasMore && (
                <button
                  onClick={loadMore.onLoadMore}
                  disabled={loadMore.loading}
                  className="btn btn-light-primary btn-sm"
                >
                  {loadMore.loading ? "Chargement…" : "Charger plus"}
                </button>
              )}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function alignClass(align?: "left" | "right" | "center"): string {
  if (align === "right") return "text-right";
  if (align === "center") return "text-center";
  return "text-left";
}

function colStyle<T>(c: ColumnDef<T>): CSSProperties {
  const style: CSSProperties = {};
  if (c.width !== undefined) {
    style.width = typeof c.width === "number" ? `${c.width}px` : c.width;
  }
  if (c.minWidth !== undefined) {
    style.minWidth = typeof c.minWidth === "number" ? `${c.minWidth}px` : c.minWidth;
  }
  return style;
}

function sortIcon(key: string, current: { key: string; dir: SortDir } | null): ReactNode {
  if (!current || current.key !== key) return <ChevronsUpDown className="h-3 w-3 text-[#b5b5c3]" />;
  return current.dir === "asc" ? (
    <ChevronUp className="h-3 w-3 text-brand-500" />
  ) : (
    <ChevronDown className="h-3 w-3 text-brand-500" />
  );
}

export type BadgeColor = "primary" | "success" | "danger" | "warning" | "info" | "light";

export function Badge({ children, color = "light" }: { children: ReactNode; color?: BadgeColor }) {
  const cls: Record<BadgeColor, string> = {
    primary: "bg-brand-50 text-brand-700",
    success: "bg-success-50 text-success-700",
    danger: "bg-danger-50 text-danger-700",
    warning: "bg-warning-50 text-warning-700",
    info: "bg-info-50 text-info-700",
    light: "bg-[#f5f8fa] text-[#5e6278]",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap ${cls[color]}`}>
      {children}
    </span>
  );
}

export function statusColor(status: string): BadgeColor {
  switch (status) {
    case "succeeded":
    case "active":
    case "complete":
    case "delivered":
      return "success";
    case "failed":
    case "suspended":
    case "abandoned":
    case "cancelled":
      return "danger";
    case "pending":
    case "processing":
    case "retrying":
      return "warning";
    case "open":
      return "info";
    case "expired":
    case "closed":
      return "light";
    default:
      return "light";
  }
}
